import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '@auth';
import { PreferredOrganizationService } from '@core/services/preferred-organization.service';
import { UnsyncedRecordsService } from '@core/services/unsynced-records.service';

/**
 * Enforces the preferred-organization rules on every main route (US 27702):
 *
 * - No preferred organization saved: a single-tenant user gets theirs automatically;
 *   a multi-tenant user MUST pick one first (redirect to /select-organization —
 *   the app is not usable before that).
 * - Preferred saved and the user is permitted for it: continue. (Opening the
 *   preferred organization by default happens once per LOGIN, in
 *   AuthService.completeLoginAfterCallback — doing it here would also undo every
 *   deliberate switch to a server-mode organization on the next navigation.)
 * - Preferred saved but the user is NOT permitted for it: when nothing is pending,
 *   they pick — a single-organization user skips the picker (Bug 28419) — and the
 *   old data is cleared by the change flow; while another user's records are still
 *   pending, they work online in their own organizations and no picker is offered.
 */
export const preferredOrganizationGuard: CanActivateFn = async () => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const preferredOrganizationService = inject(PreferredOrganizationService);
    const unsyncedRecordsService = inject(UnsyncedRecordsService);

    // authGuard (registered before this one) owns the unauthenticated case.
    if (!authService.isAuthenticated()) return true;

    const organizations = await authService.ensureOrganizationsLoaded();

    if (!preferredOrganizationService.preferred) {
        // Pre-feature browsers adopt the active organization (no-op elsewhere). The
        // constructor hook may have run before the organization list existed, so the
        // guard retries once the list is guaranteed loaded.
        await preferredOrganizationService.migrateIfNeeded(authService.currentOrganization);
    }

    const preferred = preferredOrganizationService.preferred;

    if (preferred) {
        if (preferredOrganizationService.isPreferredPermittedFor(organizations)) {
            return true;
        }

        const pendingTotal = unsyncedRecordsService.aggregatePersistedTotal(
            unsyncedRecordsService.readPersistedState());
        if (pendingTotal > 0) {
            // The change waits until the records' owner signs in and syncs; this user
            // works online in their own organizations meanwhile.
            return true;
        }

        return router.createUrlTree(['/select-organization']);
    }

    if (organizations.length === 1) {
        const active = authService.currentOrganization;
        if (active?.keycloakOrgId && active.tenantId) {
            preferredOrganizationService.setPreferred(active, 'auto-single');
        }
        // When the active organization cannot be resolved yet (e.g. offline before the
        // org list ever loaded) the user continues; the next navigation retries.
        return true;
    }

    if (organizations.length === 0) {
        // Without a loaded organization list there is nothing to decide on; never
        // trap the user behind a picker that cannot render choices.
        return true;
    }

    return router.createUrlTree(['/select-organization']);
};

/**
 * Keeps /select-organization itself honest: with a valid, permitted preferred
 * organization there is nothing to pick — go home.
 */
export const selectOrganizationPageGuard: CanActivateFn = async () => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const preferredOrganizationService = inject(PreferredOrganizationService);

    if (!authService.isAuthenticated()) return true;

    const organizations = await authService.ensureOrganizationsLoaded();
    const preferred = preferredOrganizationService.preferred;
    if (preferred && preferredOrganizationService.isPreferredPermittedFor(organizations)) {
        return router.createUrlTree(['/']);
    }
    return true;
};
